import { useState, useCallback, useRef } from "react";
import { Palette, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button, Input, Label } from "@cieda/djayma-ui";
import { MarketingImageEditor } from "@/components/admin/images";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useLocalCrud, generateId, LS_KEYS } from "./cms-store";
import { ConfirmDialog } from "./ConfirmDialog";
import { OfflineBadge } from "./OfflineBadge";
import type { BrandShowcase, BrandFormData } from "./cms-types";

// ---------------------------------------------------------------------------
// CIEDA pre-seeded brands
// ---------------------------------------------------------------------------

const CIEDA_DEFAULT_BRANDS: readonly BrandShowcase[] = [
  {
    id: "brand_countre_dairy",
    brand_name: "Countre Dairy",
    tagline: "Produits laitiers",
    description: "Lait, yaourt, cr\u00E8me",
    accent_color: "#4A90D9",
    logo_url: null,
    display_order: 1,
    created_at: new Date().toISOString(),
  },
  {
    id: "brand_yousra",
    brand_name: "Yousra",
    tagline: "Mayonnaise et riz",
    description: "Mayonnaise et riz de qualit\u00E9",
    accent_color: "#D4A535",
    logo_url: null,
    display_order: 2,
    created_at: new Date().toISOString(),
  },
  {
    id: "brand_florida",
    brand_name: "Florida",
    tagline: "Conserves alimentaires",
    description: "Conserves alimentaires de qualit\u00E9",
    accent_color: "#C75B39",
    logo_url: null,
    display_order: 3,
    created_at: new Date().toISOString(),
  },
  {
    id: "brand_kamax",
    brand_name: "KamaX / KamiX",
    tagline: "Produits d'entretien",
    description: "Produits d'entretien et de nettoyage",
    accent_color: "#2D7A4F",
    logo_url: null,
    display_order: 4,
    created_at: new Date().toISOString(),
  },
];

// ---------------------------------------------------------------------------
// Empty form default
// ---------------------------------------------------------------------------

const EMPTY_BRAND_FORM: BrandFormData = {
  brand_name: "",
  tagline: "",
  description: "",
  accent_color: "#C75B39",
  logo_url: "",
  display_order: 1,
};

// ---------------------------------------------------------------------------
// Brand form modal
// ---------------------------------------------------------------------------

function BrandFormModal({
  open,
  onClose,
  onSubmit,
  initialData,
  isSubmitting,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: BrandFormData) => void;
  readonly initialData: BrandFormData;
  readonly isSubmitting: boolean;
}) {
  const [form, setForm] = useState<BrandFormData>(initialData);

  const prevDataRef = useRef(initialData);
  if (prevDataRef.current !== initialData) {
    prevDataRef.current = initialData;
    setForm(initialData);
  }

  const updateField = <K extends keyof BrandFormData>(
    key: K,
    value: BrandFormData[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.brand_name.trim()) {
      toast.error("Le nom de la marque est obligatoire");
      return;
    }
    onSubmit(form);
  };

  const isEditing = initialData.brand_name !== "";

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={isEditing ? "Modifier la marque" : "Ajouter une marque"}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? "Chargement..."
              : isEditing
                ? "Mettre \u00E0 jour"
                : "Cr\u00E9er"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="brand-name">Nom de la marque *</Label>
          <Input
            id="brand-name"
            value={form.brand_name}
            onChange={(e) => updateField("brand_name", e.target.value)}
            placeholder="Nom de la marque"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="brand-tagline">Slogan</Label>
          <Input
            id="brand-tagline"
            value={form.tagline}
            onChange={(e) => updateField("tagline", e.target.value)}
            placeholder="Slogan de la marque"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="brand-description">Description</Label>
          <textarea
            id="brand-description"
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Description de la marque"
            rows={3}
            className="flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <MarketingImageEditor
          preset="brand"
          value={form.logo_url}
          onChange={(url) => updateField("logo_url", url)}
          label="Logo de la marque"
        />

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="brand-color">Couleur d'accent</Label>
            <div className="flex items-center gap-2">
              <input
                id="brand-color"
                type="color"
                value={form.accent_color}
                onChange={(e) => updateField("accent_color", e.target.value)}
                className="h-9 w-12 cursor-pointer rounded-md border border-stone-200"
              />
              <Input
                value={form.accent_color}
                onChange={(e) => updateField("accent_color", e.target.value)}
                placeholder="#C75B39"
                className="flex-1 font-mono text-sm"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="brand-order">Ordre d'affichage</Label>
            <Input
              id="brand-order"
              type="number"
              min={1}
              value={form.display_order}
              onChange={(e) =>
                updateField(
                  "display_order",
                  parseInt(e.target.value, 10) || 1,
                )
              }
            />
          </div>
        </div>
      </form>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Brands Tab
// ---------------------------------------------------------------------------

export function BrandsTab() {
  const crud = useLocalCrud<BrandShowcase>({
    queryKey: "cms-brand-showcases",
    apiPath: "/api/cms/brand-showcases",
    lsKey: LS_KEYS.brands,
    extractKey: "brands",
    defaults: CIEDA_DEFAULT_BRANDS,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<BrandShowcase | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BrandShowcase | null>(null);

  const openCreateModal = useCallback(() => {
    setEditingBrand(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((brand: BrandShowcase) => {
    setEditingBrand(brand);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingBrand(null);
  }, []);

  const handleSubmit = useCallback(
    (data: BrandFormData) => {
      if (editingBrand) {
        crud.updateItem({
          ...editingBrand,
          brand_name: data.brand_name,
          tagline: data.tagline || null,
          description: data.description || null,
          accent_color: data.accent_color || null,
          logo_url: data.logo_url || null,
          display_order: data.display_order,
        });
      } else {
        crud.createItem({
          id: generateId(),
          brand_name: data.brand_name,
          tagline: data.tagline || null,
          description: data.description || null,
          accent_color: data.accent_color || null,
          logo_url: data.logo_url || null,
          display_order: data.display_order,
          created_at: new Date().toISOString(),
        });
      }
      closeModal();
    },
    [editingBrand, crud, closeModal],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    crud.deleteItem(deleteTarget.id);
    setDeleteTarget(null);
  }, [deleteTarget, crud]);

  const sortedItems = [...crud.items].sort(
    (a, b) => a.display_order - b.display_order,
  );

  const formData: BrandFormData = editingBrand
    ? {
        brand_name: editingBrand.brand_name,
        tagline: editingBrand.tagline ?? "",
        description: editingBrand.description ?? "",
        accent_color: editingBrand.accent_color ?? "#C75B39",
        logo_url: editingBrand.logo_url ?? "",
        display_order: editingBrand.display_order,
      }
    : { ...EMPTY_BRAND_FORM, display_order: sortedItems.length + 1 };

  const columns: readonly ColumnDef<BrandShowcase>[] = [
    {
      id: "logo",
      header: "Logo",
      cell: (row) =>
        row.logo_url ? (
          <img
            src={row.logo_url}
            alt={row.brand_name}
            className="h-8 w-8 rounded-md border border-stone-200 object-contain"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-md border border-dashed border-stone-200 bg-stone-50">
            <Palette className="h-4 w-4 text-stone-300" />
          </div>
        ),
    },
    {
      id: "brand_name",
      header: "Marque",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.brand_name}</span>
      ),
    },
    {
      id: "tagline",
      header: "Slogan",
      cell: (row) => (
        <span className="text-sm text-stone-500">
          {row.tagline ?? "\u2014"}
        </span>
      ),
    },
    {
      id: "accent_color",
      header: "Couleur",
      cell: (row) =>
        row.accent_color ? (
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-5 w-5 rounded-full border border-stone-200"
              style={{ backgroundColor: row.accent_color }}
            />
            <span className="font-mono text-xs text-stone-500">
              {row.accent_color}
            </span>
          </div>
        ) : (
          <span className="text-xs text-stone-400">\u2014</span>
        ),
    },
    {
      id: "display_order",
      header: "Ordre",
      cell: (row) => (
        <span className="text-xs font-medium text-stone-600">
          {row.display_order}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEditModal(row)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label="Modifier"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-500"
            aria-label="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {crud.isOffline && <OfflineBadge />}
        </div>
        <Button onClick={openCreateModal} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Ajouter une marque
        </Button>
      </div>

      {sortedItems.length === 0 && !crud.isLoading ? (
        <EmptyState
          icon={<Palette className="h-6 w-6" />}
          title="Aucune marque"
          description="Ajoutez des marques pour les afficher sur le site."
          action={{ label: "Ajouter une marque", onClick: openCreateModal }}
        />
      ) : (
        <AdminDataTable
          columns={columns}
          data={sortedItems}
          isLoading={crud.isLoading}
          emptyMessage="Aucune marque trouvée."
        />
      )}

      <BrandFormModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialData={formData}
        isSubmitting={crud.isCreating || crud.isUpdating}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Supprimer la marque"
        message={`Voulez-vous vraiment supprimer la marque \u00AB ${deleteTarget?.brand_name ?? ""} \u00BB ? Cette action est irr\u00E9versible.`}
        isLoading={crud.isDeleting}
      />
    </div>
  );
}
